'use client';

import { useRef, useState } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'motion/react';
import { useLocale } from './LocaleProvider';
import { TextReveal } from './primitives/TextReveal';
import { LuxuryIcon, BellIcon } from './icons/LuxuryIcons';
import { SophiaLogo } from './SophiaLogo';

/**
 * MobileAppTeaser — CSS-only phone mockup with rotation on scroll.
 *
 * Pure markup & Tailwind — no image assets, no heavy 3D. The phone
 * rotates slightly as the user scrolls past, with a scroll-linked
 * gradient glow behind it. Inside the screen, a mock "Home" view
 * shows the user's portfolio and live alerts — selling the app story.
 *
 * Coming 2027 — Sophia for iOS & Android, TestFlight waitlist.
 */

export function MobileAppTeaser() {
  const { locale } = useLocale();
  const isAr = locale === 'ar';
  const ref = useRef<HTMLElement>(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const rotateY = useTransform(scrollYProgress, [0, 1], [22, -22]);
  const y = useTransform(scrollYProgress, [0, 1], [60, -60]);

  const copy = isAr
    ? {
        ribbon: 'الإطلاق في مايو 2026',
        eyebrow: 'تطبيق iOS و Android',
        heading: 'Sophia في جيبك.',
        sub: 'نفس المحفظة، نفس التنبيهات، نفس فاحص الأصالة — في تطبيق أصلي سريع. قائمة انتظار TestFlight مفتوحة الآن.',
        feature1: 'إشعارات لحظية عند انخفاض الأسعار',
        feature2: 'فحص الأصالة بكاميرا الهاتف',
        feature3: 'محفظة كاملة على هاتفك',
        cta: 'انضم لـ TestFlight',
        android: 'Android في نفس الشهر',
      }
    : {
        ribbon: 'Launching May 2026',
        eyebrow: 'iOS & Android app',
        heading: 'Sophia in your pocket.',
        sub: 'Same portfolio, same alerts, same authenticity scanner — in a native, fast app. TestFlight waitlist is open now.',
        feature1: 'Instant push alerts on price drops',
        feature2: 'Authenticity scan with phone camera',
        feature3: 'Full portfolio on the go',
        cta: 'Join TestFlight',
        android: 'Android same month',
      };

  return (
    <section
      ref={ref}
      className="relative py-12 sm:py-16 md:py-32 border-t border-white/5 overflow-hidden"
    >
      {/* Ambient */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-radial-violet"
      />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-6 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        {/* Copy */}
        <div>
          <div className="inline-flex items-center gap-2 mb-5">
            <span className="text-[10px] tracking-[0.3em] uppercase text-gold-soft font-semibold">
              {copy.eyebrow}
            </span>
            <span className="px-2 py-0.5 rounded-full bg-emerald/15 text-emerald-soft text-[9px] tracking-[0.25em] uppercase font-bold border border-emerald/20">
              {copy.ribbon}
            </span>
          </div>
          <TextReveal as="h2" className="font-serif text-display-sm text-cream leading-[0.98]">
            {copy.heading}
          </TextReveal>
          <p className="mt-6 text-fog-300 text-lg leading-relaxed max-w-lg">{copy.sub}</p>

          <ul className="mt-8 space-y-3">
            {[copy.feature1, copy.feature2, copy.feature3].map((f) => (
              <li key={f} className="flex items-center gap-3 text-fog-300">
                <span className="w-5 h-5 rounded-full bg-gold/15 border border-gold/30 grid place-items-center text-xs text-gold-soft">
                  ✓
                </span>
                <span className="text-[15px]">{f}</span>
              </li>
            ))}
          </ul>

          <WaitlistBar locale={locale} ctaLabel={copy.cta} androidLabel={copy.android} />
        </div>

        {/* Phone mockup */}
        <div className="relative flex items-center justify-center" style={{ perspective: '1400px' }}>
          <motion.div
            style={{ rotateY, y, transformStyle: 'preserve-3d' }}
            className="relative"
          >
            {/* Reflective floor */}
            <div
              aria-hidden
              className="absolute -bottom-20 left-1/2 -translate-x-1/2 w-[80%] h-6 rounded-[50%] bg-gold/10 blur-2xl"
            />

            {/* Phone chassis — scales down on narrow viewports so it
                doesn't get clipped by the ambient blur + padding. */}
            <div
              className="relative w-[240px] h-[480px] sm:w-[280px] sm:h-[560px] rounded-[36px] sm:rounded-[44px] p-[3px]"
              style={{
                background:
                  'linear-gradient(135deg, #e8c568 0%, #c9a96a 30%, #5a4318 60%, #c9a96a 100%)',
                boxShadow:
                  '0 45px 100px -20px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04), inset 0 0 0 1px rgba(0,0,0,0.4)',
              }}
            >
              <div
                className="relative w-full h-full rounded-[42px] bg-obsidian-900 overflow-hidden"
                dir="ltr"
              >
                {/* Notch */}
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 rounded-b-xl bg-obsidian-900 z-10" />

                {/* Status bar */}
                <div className="absolute top-2.5 inset-x-0 flex justify-between px-8 text-[10px] text-cream font-semibold z-20">
                  <span>9:41</span>
                  <span className="flex gap-1 items-center">
                    <span></span>
                    <span>􀋦</span>
                  </span>
                </div>

                {/* App content */}
                <div className="absolute inset-0 pt-12 pb-6 px-4 flex flex-col">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-[9px] tracking-[0.3em] uppercase text-gold-soft font-bold">
                        Portfolio
                      </div>
                      <div className="font-serif text-2xl text-cream leading-none mt-0.5">
                        AED 165,700
                      </div>
                      <div className="text-[10px] text-emerald-soft font-mono mt-0.5">
                        ↑ 12.4% this quarter
                      </div>
                    </div>
                    <SophiaLogo size={32} className="rounded-full" />
                  </div>

                  {/* Chart */}
                  <div className="h-16 mb-3 rounded-lg bg-obsidian-700/60 p-2 overflow-hidden">
                    <svg viewBox="0 0 200 40" className="w-full h-full" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="m-chart" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#c9a96a" stopOpacity="0.4" />
                          <stop offset="100%" stopColor="#c9a96a" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      <path
                        d="M 0 32 L 20 30 L 40 26 L 60 28 L 80 20 L 100 22 L 120 16 L 140 18 L 160 10 L 180 12 L 200 6 L 200 40 L 0 40 Z"
                        fill="url(#m-chart)"
                      />
                      <path
                        d="M 0 32 L 20 30 L 40 26 L 60 28 L 80 20 L 100 22 L 120 16 L 140 18 L 160 10 L 180 12 L 200 6"
                        fill="none"
                        stroke="#c9a96a"
                        strokeWidth="1.2"
                      />
                    </svg>
                  </div>

                  {/* Piece cards */}
                  {([
                    { icon: 'handbag' as const, brand: 'Hermès', model: 'Birkin 25', value: 61500, change: '+2.8%' },
                    { icon: 'watch' as const, brand: 'Rolex', model: 'Submariner', value: 65000, change: '+12%' },
                    { icon: 'wallet' as const, brand: 'Chanel', model: 'Classic Flap', value: 36960, change: '-1.2%' },
                  ]).map((p) => (
                    <div
                      key={p.model}
                      className="flex items-center gap-3 px-2 py-2 mb-1.5 rounded-lg hover:bg-white/5 transition"
                    >
                      <div className="w-8 h-8 rounded-md bg-obsidian-700 grid place-items-center text-gold-soft">
                        <LuxuryIcon kind={p.icon} size={20} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] text-fog-500 leading-tight">{p.brand}</div>
                        <div className="text-xs text-cream leading-tight">{p.model}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-cream font-mono">
                          {(p.value / 1000).toFixed(1)}k
                        </div>
                        <div
                          className={`text-[10px] font-mono ${
                            p.change.startsWith('+') ? 'text-emerald-soft' : 'text-rose'
                          }`}
                        >
                          {p.change}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Alert pill at bottom */}
                  <div className="mt-auto rounded-xl bg-gradient-to-r from-rose/20 to-rose/5 border border-rose/20 p-2.5 flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-rose/30 grid place-items-center text-rose-soft">
                      <BellIcon size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] text-rose-soft font-bold">PRICE DROP</div>
                      <div className="text-[10px] text-cream truncate">
                        Kelly 28 -10% on Vestiaire
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/**
 * Small inline email form used by the TestFlight CTA. Posts to
 * /api/waitlist with feature='mobile_app'; optimistic success.
 */
function WaitlistBar({
  locale,
  ctaLabel,
  androidLabel,
}: {
  locale: 'en' | 'ar';
  ctaLabel: string;
  androidLabel: string;
}) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setSent(true);
    try {
      await fetch('https://api.mysophia.tech/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          feature: 'mobile_app',
          source: 'landing_mobile',
          locale,
        }),
      });
    } catch {
      /* silent */
    }
  }

  return (
    <div className="mt-8">
      <AnimatePresence mode="wait">
        {!sent ? (
          <motion.form
            key="form"
            onSubmit={onSubmit}
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-wrap gap-2 items-center"
          >
            <label htmlFor="mobile-waitlist-email" className="sr-only">
              Email
            </label>
            <input
              id="mobile-waitlist-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={locale === 'ar' ? 'بريدك الإلكتروني' : 'your@email.com'}
              className="flex-1 min-w-[200px] px-4 py-3 rounded-pill bg-obsidian-900 border border-white/10 text-cream text-sm placeholder:text-fog-500 focus:border-gold/40 focus:outline-none"
              data-cursor="text"
              dir="ltr"
            />
            <button
              type="submit"
              data-cursor="cta"
              className="px-5 py-3 rounded-pill bg-cta-gradient text-white text-sm font-semibold shadow-glow hover:bg-cta-gradient-hover transition shrink-0"
            >
              {ctaLabel}
            </button>
            <span className="inline-flex items-center gap-2 px-4 py-3 rounded-pill bg-white/5 border border-white/10 text-fog-300 text-sm shrink-0">
              {androidLabel}
            </span>
          </motion.form>
        ) : (
          <motion.div
            key="sent"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-pill bg-emerald/15 border border-emerald/20 text-emerald-soft text-sm"
          >
            <span>✓</span>
            {locale === 'ar' ? 'تم — سنرسل الدعوة عند الإطلاق' : 'Thanks — we\'ll invite you at launch'}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
